import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { t } from 'ember-intl';
import { or, not } from 'ember-truth-helpers';
import { queue } from '@nullvoxpopuli/ember-composable-helpers';
import can from 'ember-can/helpers/can';
import set from 'ember-set-helper/helpers/set';
import {
  HdsPageHeader,
  HdsButton,
  HdsTable,
  HdsLinkInline,
  HdsBadge,
  HdsIcon,
  HdsDropdown,
  HdsModal,
  HdsButtonSet,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  LimitBanner,
  OperationRefreshRoute,
  Flex,
  Typography,
  ModalConfirm,
  ZeroState,
} from 'core/components';
import {
  optionForSourceState,
  resourceIsBusy,
  resourceTypeFor,
} from 'core/helpers';
import { boxPadding } from 'core/modifiers';
import { TYPE_NETWORK_TRANSIT_GATEWAY_ATTACHMENT } from 'common/utils/cloud-resource-types';
import Container from 'hcp/components/page/hvns/detail/container';

/**
 *
 * `model` will be a model containing the transitGatewayAttachments list, the current organization, and project.
 * @argument model;
 * @type {Object}
 *
 */
/**
 *
 * `PageHvnTransitGatewayAttachmentsList` renders the HVN TransitGatewayAttachments List page.
 *
 *
 * ```
 * <Page::HvnTransitGatewayAttachments::List
 *   @model={{model}}
 * />
 * ```
 *
 * @class PageHvnTransitGatewayAttachmentsList
 *
 */
export default class PageHvnTransitGatewayAttachmentsList extends Component {
  @service api;
  @service operation;

  @tracked showDeleteModal = false;
  @tracked transitGatewayAttachmentToDelete;
  @tracked allowDelete = false;

  get transitGatewayAttachmentType() {
    return TYPE_NETWORK_TRANSIT_GATEWAY_ATTACHMENT;
  }

  deleteTransitGatewayAttachment = task(async (evt) => {
    evt.preventDefault();
    let hvnId = this.args.model.network.id;
    let { organizationId, projectId } = this.args.model.network.location;
    let { id } = this.transitGatewayAttachmentToDelete;
    let operation;

    try {
      let resp = await this.api.network.deleteTGWAttachment(
        organizationId,
        projectId,
        hvnId,
        id
      );
      operation = resp.operation;
    } catch (e) {
      if (macroCondition(isDevelopingApp())) {
        console.error(e);
      }
      // TODO - make this a flash message
      throw e;
    }
    this.showDeleteModal = false;
    this.operation.addOperation(operation);
  });

  <template>
    <Container @model={{@model}}>
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.Title>
              {{t "components.page.hvn-transit-gateway-attachments.list.title"}}
            </PH.Title>
            {{#unless
              (resourceIsBusy @model.network type=(resourceTypeFor "network"))
            }}
              {{#if
                (can "create network-tgw-attachment" @model.tgwAttachments)
              }}
                <PH.Actions>
                  <HdsButton
                    @color="primary"
                    @icon="plus"
                    @iconPosition="leading"
                    @route="cloud.orgs.detail.projects.detail.hvns.detail.transit-gateway-attachments.create"
                    @text={{t
                      "components.page.hvn-transit-gateway-attachments.create.action"
                    }}
                    data-test-transit-gateway-attachment-create
                  />
                </PH.Actions>
              {{/if}}
            {{/unless}}
          </HdsPageHeader>
        </:header>
        <:alert>
          <LimitBanner
            @variant={{this.transitGatewayAttachmentType}}
            @count={{@model.tgwAttachments.length}}
            @padding="0"
          />
        </:alert>
        <:body>
          <div class="hvnTransitGatewayAttachmentsList">
            <OperationRefreshRoute
              @route="cloud.orgs.detail.projects.detail.hvns.detail.transit-gateway-attachments.list"
              @model={{@model.tgwAttachments}}
              @resourceType={{resourceTypeFor "network-tgw-attachment"}}
            />
            {{#if @model.tgwAttachments.length}}
              <HdsTable
                @valign="middle"
                @caption={{t
                  "components.page.hvn-transit-gateway-attachments.list.title"
                }}
              >
                <:head as |H|>
                  <H.Tr>
                    <H.Th>
                      {{t
                        "components.page.hvn-transit-gateway-attachments.list.table.headers.attachment-id"
                      }}
                    </H.Th>
                    <H.Th>
                      {{t
                        "components.page.hvn-transit-gateway-attachments.list.table.headers.status"
                      }}
                    </H.Th>
                    <H.Th>
                      {{t
                        "components.page.hvn-transit-gateway-attachments.list.table.headers.account-id"
                      }}
                    </H.Th>
                    <H.Th>
                      {{t
                        "components.page.hvn-transit-gateway-attachments.list.table.headers.transit-gateway-id"
                      }}
                    </H.Th>
                    <H.Th>
                      {{t
                        "components.page.hvn-transit-gateway-attachments.list.table.headers.vpc-region"
                      }}
                    </H.Th>
                    <H.Th />
                  </H.Tr>
                </:head>
                <:body as |B|>
                  {{#each @model.tgwAttachments key="id" as |attachment|}}
                    <B.Tr data-test-transit-gateway-attachment-item>
                      <B.Td>
                        <HdsLinkInline
                          @color="secondary"
                          @models={{array attachment.id}}
                          @route="cloud.orgs.detail.projects.detail.hvns.detail.transit-gateway-attachments.detail"
                          class="hds-font-weight-medium"
                        >
                          {{attachment.id}}
                        </HdsLinkInline>
                      </B.Td>
                      <B.Td>
                        {{#let
                          (optionForSourceState
                            "hvn_transit_gateway_attachment" attachment.state
                          )
                          as |option|
                        }}
                          <HdsBadge
                            @text={{t option.text}}
                            @color={{option.color}}
                            @icon={{option.icon}}
                            @isIconOnly={{option.isIconOnly}}
                            @size={{option.size}}
                            @type={{option.type}}
                          />
                        {{/let}}
                      </B.Td>
                      <B.Td>
                        {{#let
                          (optionForSourceState "region" "aws")
                          as |option|
                        }}
                          <Flex
                            @alignItems="center"
                            @direction="row"
                            @gap="2xs"
                          >
                            <HdsIcon
                              @name={{option.icon}}
                              @isInline={{true}}
                              data-test-provider-logo
                            />
                            <span>
                              {{attachment.providerData.awsData.accountId}}
                            </span>
                          </Flex>
                        {{/let}}
                      </B.Td>
                      <B.Td>
                        {{attachment.providerData.awsData.tgwId}}
                      </B.Td>
                      <B.Td>
                        {{attachment.location.region.region}}
                      </B.Td>
                      <B.Td>
                        {{#unless
                          (resourceIsBusy
                            attachment type="hashicorp.network.tgw-attachment"
                          )
                        }}
                          <HdsDropdown as |dd|>
                            <dd.ToggleIcon
                              @icon="more-horizontal"
                              @text={{t "components.form.more"}}
                              @hasChevron={{false}}
                            />
                            <dd.Interactive
                              @text={{t "components.form.delete"}}
                              @color="critical"
                              @icon="trash"
                              id="modal-dialog-open-button-{{attachment.id}}"
                              data-test-delete-button
                              {{on
                                "click"
                                (queue
                                  (set
                                    this
                                    "transitGatewayAttachmentToDelete"
                                    attachment
                                  )
                                  (set this "showDeleteModal" true)
                                )
                              }}
                            />
                          </HdsDropdown>
                        {{/unless}}
                      </B.Td>
                    </B.Tr>
                  {{/each}}
                </:body>
              </HdsTable>
              {{#if this.showDeleteModal}}
                <HdsModal
                  @color="critical"
                  @onClose={{queue
                    (set this "showDeleteModal")
                    (set this "allowDelete")
                  }}
                  as |M|
                >
                  <M.Header @icon="alert-triangle">
                    {{t
                      "components.page.hvn-transit-gateway-attachments.list.delete-modal.title"
                    }}
                  </M.Header>
                  <M.Body>
                    <Typography
                      @variant="body1"
                      class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                      {{boxPadding "0 0 md 0"}}
                    >
                      {{t
                        "components.page.hvn-transit-gateway-attachments.list.delete-modal.warning"
                        tgwId=this.transitGatewayAttachmentToDelete.providerData.awsData.tgwId
                        htmlSafe=true
                      }}
                    </Typography>
                    <Typography
                      @variant="body1"
                      class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                      {{boxPadding "0 0 md 0"}}
                    >
                      {{t
                        "components.page.hvn-transit-gateway-attachments.list.delete-modal.recreation"
                      }}
                    </Typography>
                    <form
                      id="delete"
                      class="hcp-hds-form"
                      {{on
                        "submit"
                        this.deleteTransitGatewayAttachment.perform
                      }}
                    >
                      <ModalConfirm
                        @onAllowedChange={{set this "allowDelete"}}
                      />
                    </form>
                  </M.Body>
                  <M.Footer as |F|>
                    <HdsButtonSet>
                      <HdsButton
                        @color="critical"
                        @text={{t "components.form.delete"}}
                        disabled={{or
                          (not this.allowDelete)
                          this.deleteTransitGatewayAttachment.isRunning
                        }}
                        form="delete"
                        type="submit"
                        data-test-confirm-delete
                      />
                      <HdsButton
                        @color="secondary"
                        @text={{t "components.form.cancel"}}
                        disabled={{this.deleteTransitGatewayAttachment.isRunning}}
                        {{on "click" F.close}}
                      />
                    </HdsButtonSet>
                  </M.Footer>
                </HdsModal>
              {{/if}}
            {{else}}
              <ZeroState as |ZS|>
                <ZS.Header>
                  {{t
                    "components.page.hvn-transit-gateway-attachments.list.empty.header"
                  }}
                </ZS.Header>
                <ZS.Message>
                  {{t
                    "components.page.hvn-transit-gateway-attachments.list.empty.message"
                  }}
                </ZS.Message>
                {{#if
                  (can "create network-tgw-attachment" @model.tgwAttachments)
                }}
                  <ZS.Action>
                    <HdsLinkInline
                      @route="cloud.orgs.detail.projects.detail.hvns.detail.transit-gateway-attachments.create"
                    >
                      {{t
                        "components.page.hvn-transit-gateway-attachments.list.empty.action"
                      }}
                    </HdsLinkInline>
                  </ZS.Action>
                {{/if}}
              </ZeroState>
            {{/if}}
          </div>
        </:body>
      </CoreLayoutMain>
    </Container>
  </template>
}
